import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { AppLayout } from './components/layout/AppLayout';
import { AuthLayout } from './components/layout/AuthLayout';
import { ProtectedRoute } from './routes/ProtectedRoute';
import { FeatureRoute } from './routes/FeatureRoute';
import { lazy, Suspense } from 'react';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { PermissionGuard } from './components/common/PermissionGuard';

// Auth pages
import LoginPage from './pages/auth/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';

// Core pages (always loaded)
import DashboardPage from './pages/dashboard/DashboardPage';
import AssetsPage from './pages/assets/AssetsPage';
import AssetDetailPage from './pages/assets/AssetDetailPage';
import AssetFormPage from './pages/assets/AssetFormPage';
import AssetImportPage from './pages/assets/AssetImportPage';
import EpcsPage from './pages/epcs/EpcsPage';
import EpcImportPage from './pages/epcs/EpcImportPage';
import InventoryPage from './pages/inventory/InventoryPage';
import InventoryDetailPage from './pages/inventory/InventoryDetailPage';
import IncidentsPage from './pages/incidents/IncidentsPage';
import ApprovalCenterPage from './pages/approvals/ApprovalCenterPage';
import ApprovalRequestDetailPage from './pages/approvals/ApprovalRequestDetailPage';
import UsersPage from './pages/catalogs/users/UsersPage';
import LocationsPage from './pages/catalogs/locations/LocationsPage';

// Lazy loaded feature pages (Toca only)
const RequisitionsPage = lazy(() => import('./pages/requisitions/RequisitionsPage'));
const RecoveryPage = lazy(() => import('./pages/recovery/RecoveryPage'));
const DamagePage = lazy(() => import('./pages/damage/DamagePage'));
const RepairsPage = lazy(() => import('./pages/repairs/RepairsPage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
// Phase 7a — Reports Hub for engine-driven (dynamic) presets. Parallel
// to the legacy TOCA ReportsPage; never replaces it (ADR-020).
const ReportsHubPage = lazy(() => import('./pages/reports/ReportsHubPage'));
const CustomReportBuilderPage = lazy(() => import('./pages/reports/CustomReportBuilderPage'));
// Phase 11 — generic catch-all renderer for tenant-authored modules.
// Routes itself to one of four built-in templates by ModuleType.
const DynamicModulePage = lazy(() => import('./pages/dynamic/DynamicModulePage'));
const ClientsPage = lazy(() => import('./pages/catalogs/clients/ClientsPage'));

// Client Hub pages. The route-level switchers pick the legacy TOCA page or
// the workflow variant per tenant (client-centric nav restructure). TOCA/SGS
// resolve to the legacy pages unchanged.
const ClientsHubRoute = lazy(() =>
  import('./pages/clients-hub/ClientsHubRouter').then((m) => ({ default: m.ClientsHubRoute }))
);
const ClientWorkspaceRoute = lazy(() =>
  import('./pages/clients-hub/ClientsHubRouter').then((m) => ({ default: m.ClientWorkspaceRoute }))
);
const TransfersPage = lazy(() => import('./pages/transfers/TransfersPage'));

// Maintenance pages (feature flag controlled)
const MaintenanceDashboardPage = lazy(() => import('./pages/maintenance/MaintenanceDashboardPage'));
const WorkOrdersPage = lazy(() => import('./pages/maintenance/WorkOrdersPage'));
const WorkOrderFormPage = lazy(() => import('./pages/maintenance/WorkOrderFormPage'));
const PlansPage = lazy(() => import('./pages/maintenance/PlansPage'));
const PlanFormPage = lazy(() => import('./pages/maintenance/PlanFormPage'));
const PartsPage = lazy(() => import('./pages/maintenance/PartsPage'));
const PartFormPage = lazy(() => import('./pages/maintenance/PartFormPage'));
const AlertsPage = lazy(() => import('./pages/maintenance/AlertsPage'));
const SchedulingPage = lazy(() => import('./pages/maintenance/scheduling/SchedulingPage'));
const CatalogHubPage = lazy(() => import('./pages/maintenance/catalogs/CatalogHubPage'));

// Production Operations pages (feature flag controlled)
const FleetOverviewPage = lazy(() => import('./pages/fleet/FleetOverviewPage'));
const VehicleDetailPage = lazy(() => import('./pages/fleet/VehicleDetailPage'));
const ProductionOrdersPage = lazy(() => import('./pages/production-orders/ProductionOrdersPage'));
const ProductionOrderFormPage = lazy(() => import('./pages/production-orders/ProductionOrderFormPage'));
const ProductionOrderDetailPage = lazy(() => import('./pages/production-orders/ProductionOrderDetailPage'));
const WarehouseDashboardPage = lazy(() => import('./pages/warehouse/WarehouseDashboardPage'));

// Billing & CFDI 4.0 e-invoicing (TOCA-only, gated by `billing` feature flag)
const BillingDashboardPage = lazy(() => import('./pages/billing/BillingDashboardPage'));
const FiscalSetupPage = lazy(() => import('./pages/billing/FiscalSetupPage'));
const TariffPlansPage = lazy(() => import('./pages/billing/TariffPlansPage'));
const TariffAssignmentsPage = lazy(() => import('./pages/billing/TariffAssignmentsPage'));
const TariffPlanEditorPage = lazy(() => import('./pages/billing/TariffPlanEditorPage'));
const InventoryStaysPage = lazy(() => import('./pages/billing/InventoryStaysPage'));
const PalletStayDetailPage = lazy(() => import('./pages/billing/PalletStayDetailPage'));
const BillingPeriodsPage = lazy(() => import('./pages/billing/BillingPeriodsPage'));
const InvoicesPage = lazy(() => import('./pages/billing/InvoicesPage'));
const InvoiceDetailPage = lazy(() => import('./pages/billing/InvoiceDetailPage'));
const BillingAuditPage = lazy(() => import('./pages/billing/BillingAuditPage'));
const NewInvoicePage = lazy(() => import('./pages/billing/NewInvoicePage'));
const PacConfigPage = lazy(() => import('./pages/billing/PacConfigPage'));
const PaymentComplementPage = lazy(() => import('./pages/billing/PaymentComplementPage'));
const CreditNotePage = lazy(() => import('./pages/billing/CreditNotePage'));
const CartaPortePage = lazy(() => import('./pages/billing/CartaPortePage'));

// Pallet Manufacture (TOCA-only, gated by `palletManufacture` feature flag + module)
const ProvidersPage = lazy(() => import('./pages/pallet-manufacture/ProvidersPage'));
const ManufactureOrdersPage = lazy(() => import('./pages/pallet-manufacture/ManufactureOrdersPage'));
const ManufactureOrderDetailPage = lazy(
  () => import('./pages/pallet-manufacture/ManufactureOrderDetailPage')
);

// Pallet Incidents (TOCA-only: client losses / quality returns). Occupies the
// same "Incidentes" sidebar slot as the inventory incidents module, which is
// why the two flags are mutually exclusive per tenant.
const PalletIncidentsPage = lazy(() => import('./pages/pallet-incidents/PalletIncidentsPage'));

// Roles management
const RolesPage = lazy(() => import('./pages/catalogs/roles/RolesPage'));

// Admin - Metadata pages
const SchemaListPage = lazy(() => import('./pages/admin/metadata/SchemaListPage'));
const SchemaBuilderPage = lazy(() => import('./pages/admin/metadata/SchemaBuilderPage'));

// Admin - EPC Prefix Filters
const SystemConfigPage = lazy(() => import('./pages/admin/SystemConfigPage'));

// Admin - Device Management
const DevicesPage = lazy(() => import('./pages/admin/devices/DevicesPage'));
const DeviceFormPage = lazy(() => import('./pages/admin/devices/DeviceFormPage'));
const DeviceDetailPage = lazy(() => import('./pages/admin/devices/DeviceDetailPage'));

// Admin - Dynamic Workflows (Phase 2 + Phase 3 designer)
const WorkflowsListPage = lazy(() => import('./pages/admin/workflows/WorkflowsListPage'));
const WorkflowImportPage = lazy(() => import('./pages/admin/workflows/WorkflowImportPage'));
const WorkflowBindingsPage = lazy(() => import('./pages/admin/workflows/WorkflowBindingsPage'));
const WorkflowDetailPage = lazy(() => import('./pages/admin/workflows/WorkflowDetailPage'));
const WorkflowDesignerPage = lazy(() => import('./pages/admin/workflows/WorkflowDesignerPage'));
const WorkflowMigratePage = lazy(() => import('./pages/admin/workflows/WorkflowMigratePage'));

// Operations - Dynamic Workflow Shipments (Phase 2.5)
const ShipmentsPage = lazy(() => import('./pages/shipments/ShipmentsPage'));
const ShipmentDetailPage = lazy(() => import('./pages/shipments/ShipmentDetailPage'));
const OrdersPage = lazy(() => import('./pages/orders/OrdersPage'));
const OrderDetailPage = lazy(() => import('./pages/orders/OrderDetailPage'));
// B-PULL — ERP pull connector admin (scheduled order import from an ERP).
const ErpConnectionsPage = lazy(() => import('./pages/orders/ErpConnectionsPage'));

// Assets — mapping-driven import wizard + reusable import profiles (lazy).
// The wizard re-POSTs the picked file on detect/dry-run/commit; profiles let
// operators re-run a saved mapping month after month.
const AssetImportWizardPage = lazy(() => import('./pages/assets/AssetImportWizardPage'));
const ImportProfilesPage = lazy(() => import('./pages/assets/ImportProfilesPage'));

function App() {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      </Route>

      {/* Protected routes */}
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        {/* Dashboard */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* Assets */}
        <Route path="/assets" element={<AssetsPage />} />
        <Route path="/assets/new" element={<AssetFormPage />} />
        {/* File-based import (simple importer, mapping-driven wizard and its
            reusable profiles), gated by `assetImport` (off on TOCA). Registered
            before /assets/:id so the literal /assets/import/* segments are
            never captured as an asset id. */}
        <Route
          path="/assets/import"
          element={
            <FeatureRoute feature="assetImport">
              <AssetImportPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/assets/import/wizard"
          element={
            <FeatureRoute feature="assetImport">
              <Suspense fallback={<LoadingSpinner />}>
                <AssetImportWizardPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/assets/import/profiles"
          element={
            <FeatureRoute feature="assetImport">
              <Suspense fallback={<LoadingSpinner />}>
                <ImportProfilesPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route path="/assets/:id" element={<AssetDetailPage />} />
        <Route path="/assets/:id/edit" element={<AssetFormPage />} />

        {/* EPCs */}
        <Route path="/epcs" element={<EpcsPage />} />
        <Route path="/epcs/import" element={<EpcImportPage />} />

        {/* Inventory */}
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/inventory/:id" element={<InventoryDetailPage />} />

        {/* Inventory-process incidents (SGS / Demo). Guarded: this was a bare
            route, so hiding its nav entry left it reachable by typing the URL —
            which matters now that TOCA runs the pallet-incidents module in the
            same "Incidentes" slot and has this flag off. */}
        <Route
          path="/incidents"
          element={
            <FeatureRoute feature="incidents">
              <IncidentsPage />
            </FeatureRoute>
          }
        />

        {/* Change Governance — the Approval Center reclaims /approvals.
            Guarded, unlike the old stub route, which was reachable by URL even
            with its nav entry hidden. */}
        <Route
          path="/approvals"
          element={
            <FeatureRoute feature="approvalsV2">
              <ApprovalCenterPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/approvals/:id"
          element={
            <FeatureRoute feature="approvalsV2">
              <ApprovalRequestDetailPage />
            </FeatureRoute>
          }
        />

        {/* Requisitions (Toca only) */}
        <Route
          path="/requisitions/*"
          element={
            <FeatureRoute feature="requisitions">
              <Suspense fallback={<LoadingSpinner />}>
                <RequisitionsPage />
              </Suspense>
            </FeatureRoute>
          }
        />

        {/* Recovery (Toca only) */}
        <Route
          path="/recovery/*"
          element={
            <FeatureRoute feature="recovery">
              <Suspense fallback={<LoadingSpinner />}>
                <RecoveryPage />
              </Suspense>
            </FeatureRoute>
          }
        />

        {/* Transfers (Toca only) */}
        <Route
          path="/transfers/*"
          element={
            <FeatureRoute feature="clients">
              <Suspense fallback={<LoadingSpinner />}>
                <TransfersPage />
              </Suspense>
            </FeatureRoute>
          }
        />

        {/* Damage (Toca only) */}
        <Route
          path="/damage/*"
          element={
            <FeatureRoute feature="damage">
              <Suspense fallback={<LoadingSpinner />}>
                <DamagePage />
              </Suspense>
            </FeatureRoute>
          }
        />

        {/* Repairs (Toca only) */}
        <Route
          path="/repairs/*"
          element={
            <FeatureRoute feature="repair">
              <Suspense fallback={<LoadingSpinner />}>
                <RepairsPage />
              </Suspense>
            </FeatureRoute>
          }
        />

        {/* Reports (Toca only) */}
        <Route
          path="/reports/*"
          element={
            <FeatureRoute feature="reports">
              <Suspense fallback={<LoadingSpinner />}>
                <ReportsPage />
              </Suspense>
            </FeatureRoute>
          }
        />

        {/* Phase 7a — Reports Hub for engine-driven presets. Reachable
            independently of features.reports (which gates the legacy
            TOCA reports). When the hub's API call returns an empty
            template list, the page shows a "no presets registered"
            placeholder rather than 404. */}
        <Route
          path="/reports-hub"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <ReportsHubPage />
            </Suspense>
          }
        />

        {/* Phase 7b Step 2b — Custom report builder. Lives alongside the
            hub; the hub picks presets, this page picks dimensions /
            metrics from the catalog and builds a per-tenant saved spec. */}
        <Route
          path="/reports-hub/custom"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <CustomReportBuilderPage />
            </Suspense>
          }
        />

        {/* Client Hub. The switcher renders the legacy TOCA page or the
            workflow variant per tenant (client-centric nav restructure). */}
        <Route
          path="/clients-hub"
          element={
            <FeatureRoute feature="clients">
              <Suspense fallback={<LoadingSpinner />}>
                <ClientsHubRoute />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/clients-hub/:clientId/*"
          element={
            <FeatureRoute feature="clients">
              <Suspense fallback={<LoadingSpinner />}>
                <ClientWorkspaceRoute />
              </Suspense>
            </FeatureRoute>
          }
        />

        {/* Maintenance (feature flag controlled) */}
        <Route
          path="/maintenance"
          element={
            <FeatureRoute feature="maintenance">
              <Suspense fallback={<LoadingSpinner />}>
                <MaintenanceDashboardPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/maintenance/work-orders"
          element={
            <FeatureRoute feature="maintenance">
              <Suspense fallback={<LoadingSpinner />}>
                <WorkOrdersPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/maintenance/work-orders/new"
          element={
            <FeatureRoute feature="maintenance">
              <Suspense fallback={<LoadingSpinner />}>
                <WorkOrderFormPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/maintenance/work-orders/:id"
          element={
            <FeatureRoute feature="maintenance">
              <Suspense fallback={<LoadingSpinner />}>
                <WorkOrderFormPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/maintenance/work-orders/:id/edit"
          element={
            <FeatureRoute feature="maintenance">
              <Suspense fallback={<LoadingSpinner />}>
                <WorkOrderFormPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/maintenance/plans"
          element={
            <FeatureRoute feature="maintenance">
              <Suspense fallback={<LoadingSpinner />}>
                <PlansPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/maintenance/plans/new"
          element={
            <FeatureRoute feature="maintenance">
              <Suspense fallback={<LoadingSpinner />}>
                <PlanFormPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/maintenance/plans/:id/edit"
          element={
            <FeatureRoute feature="maintenance">
              <Suspense fallback={<LoadingSpinner />}>
                <PlanFormPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/maintenance/parts"
          element={
            <FeatureRoute feature="maintenance">
              <Suspense fallback={<LoadingSpinner />}>
                <PartsPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/maintenance/parts/new"
          element={
            <FeatureRoute feature="maintenance">
              <Suspense fallback={<LoadingSpinner />}>
                <PartFormPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/maintenance/parts/:id/edit"
          element={
            <FeatureRoute feature="maintenance">
              <Suspense fallback={<LoadingSpinner />}>
                <PartFormPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/maintenance/alerts"
          element={
            <FeatureRoute feature="maintenance">
              <Suspense fallback={<LoadingSpinner />}>
                <AlertsPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/maintenance/scheduling"
          element={
            <FeatureRoute feature="maintenance">
              <Suspense fallback={<LoadingSpinner />}>
                <SchedulingPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        {/*
          Catalog hub is a cross-cutting concern (asset classes, problem codes,
          units of measure, etc.) — not maintenance-specific. Mounted at
          /catalogs; the old /maintenance/catalogs path redirects so existing
          bookmarks still work.
        */}
        <Route
          path="/maintenance/catalogs"
          element={<Navigate to="/catalogs" replace />}
        />

        {/* Production Operations (feature flag controlled) */}
        <Route
          path="/fleet"
          element={
            <FeatureRoute feature="productionOperations">
              <Suspense fallback={<LoadingSpinner />}>
                <FleetOverviewPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/fleet/:vehicleId"
          element={
            <FeatureRoute feature="productionOperations">
              <Suspense fallback={<LoadingSpinner />}>
                <VehicleDetailPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/fleet/:vehicleId/trips/:tripId"
          element={
            <FeatureRoute feature="productionOperations">
              <Suspense fallback={<LoadingSpinner />}>
                <VehicleDetailPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/fleet/:vehicleId/trips/:tripId/checkpoint/:checkpointId"
          element={
            <FeatureRoute feature="productionOperations">
              <Suspense fallback={<LoadingSpinner />}>
                <VehicleDetailPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/production-orders"
          element={
            <FeatureRoute feature="productionOperations">
              <Suspense fallback={<LoadingSpinner />}>
                <ProductionOrdersPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/production-orders/new"
          element={
            <FeatureRoute feature="productionOperations">
              <Suspense fallback={<LoadingSpinner />}>
                <ProductionOrderFormPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/production-orders/:id"
          element={
            <FeatureRoute feature="productionOperations">
              <Suspense fallback={<LoadingSpinner />}>
                <ProductionOrderDetailPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/production-orders/:id/edit"
          element={
            <FeatureRoute feature="productionOperations">
              <Suspense fallback={<LoadingSpinner />}>
                <ProductionOrderFormPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/warehouse"
          element={
            <FeatureRoute feature="productionOperations">
              <Suspense fallback={<LoadingSpinner />}>
                <WarehouseDashboardPage />
              </Suspense>
            </FeatureRoute>
          }
        />

        {/* Billing & CFDI 4.0 (TOCA-only, feature-gated) */}
        <Route
          path="/billing"
          element={
            <FeatureRoute feature="billing">
              <PermissionGuard module="billing" strict>
                <Suspense fallback={<LoadingSpinner />}>
                  <BillingDashboardPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/billing/fiscal"
          element={
            <FeatureRoute feature="billing">
              <PermissionGuard module="billing" strict>
                <Suspense fallback={<LoadingSpinner />}>
                  <FiscalSetupPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/billing/assignments"
          element={
            <FeatureRoute feature="billing">
              <PermissionGuard module="billing" strict>
                <Suspense fallback={<LoadingSpinner />}>
                  <TariffAssignmentsPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/billing/tariffs"
          element={
            <FeatureRoute feature="billing">
              <PermissionGuard module="billing" strict>
                <Suspense fallback={<LoadingSpinner />}>
                  <TariffPlansPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/billing/tariffs/:id"
          element={
            <FeatureRoute feature="billing">
              <PermissionGuard module="billing" strict>
                <Suspense fallback={<LoadingSpinner />}>
                  <TariffPlanEditorPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/billing/stays"
          element={
            <FeatureRoute feature="billing">
              <PermissionGuard module="billing" strict>
                <Suspense fallback={<LoadingSpinner />}>
                  <InventoryStaysPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/billing/stays/:id"
          element={
            <FeatureRoute feature="billing">
              <PermissionGuard module="billing" strict>
                <Suspense fallback={<LoadingSpinner />}>
                  <PalletStayDetailPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/billing/periods"
          element={
            <FeatureRoute feature="billing">
              <PermissionGuard module="billing" strict>
                <Suspense fallback={<LoadingSpinner />}>
                  <BillingPeriodsPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/billing/pac"
          element={
            <FeatureRoute feature="billing">
              <PermissionGuard module="billing" strict>
                <Suspense fallback={<LoadingSpinner />}>
                  <PacConfigPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/billing/invoices/new"
          element={
            <FeatureRoute feature="billing">
              <PermissionGuard module="billing" strict>
                <Suspense fallback={<LoadingSpinner />}>
                  <NewInvoicePage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/billing/payments/new"
          element={
            <FeatureRoute feature="billing">
              <PermissionGuard module="billing" strict>
                <Suspense fallback={<LoadingSpinner />}>
                  <PaymentComplementPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/billing/invoices"
          element={
            <FeatureRoute feature="billing">
              <PermissionGuard module="billing" strict>
                <Suspense fallback={<LoadingSpinner />}>
                  <InvoicesPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/billing/invoices/:id/credit-note"
          element={
            <FeatureRoute feature="billing">
              <PermissionGuard module="billing" strict>
                <Suspense fallback={<LoadingSpinner />}>
                  <CreditNotePage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/billing/invoices/:id/carta-porte"
          element={
            <FeatureRoute feature="billing">
              <PermissionGuard module="billing" strict>
                <Suspense fallback={<LoadingSpinner />}>
                  <CartaPortePage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/billing/invoices/:id"
          element={
            <FeatureRoute feature="billing">
              <PermissionGuard module="billing" strict>
                <Suspense fallback={<LoadingSpinner />}>
                  <InvoiceDetailPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/billing/audit"
          element={
            <FeatureRoute feature="billing">
              <PermissionGuard module="billing" strict>
                <Suspense fallback={<LoadingSpinner />}>
                  <BillingAuditPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />

        {/* Pallet Incidents (TOCA-only, feature-gated + permission-gated) */}
        <Route
          path="/pallet-incidents"
          element={
            <FeatureRoute feature="palletIncidents">
              <PermissionGuard module="incidentes_tarimas">
                <Suspense fallback={<LoadingSpinner />}>
                  <PalletIncidentsPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />

        {/* Pallet Manufacture (TOCA-only, feature-gated + permission-gated) */}
        <Route
          path="/pallet-manufacture/providers"
          element={
            <FeatureRoute feature="palletManufacture">
              <PermissionGuard module="palletManufacture">
                <Suspense fallback={<LoadingSpinner />}>
                  <ProvidersPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/pallet-manufacture/orders"
          element={
            <FeatureRoute feature="palletManufacture">
              <PermissionGuard module="palletManufacture">
                <Suspense fallback={<LoadingSpinner />}>
                  <ManufactureOrdersPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />
        <Route
          path="/pallet-manufacture/orders/:id"
          element={
            <FeatureRoute feature="palletManufacture">
              <PermissionGuard module="palletManufacture">
                <Suspense fallback={<LoadingSpinner />}>
                  <ManufactureOrderDetailPage />
                </Suspense>
              </PermissionGuard>
            </FeatureRoute>
          }
        />

        {/* Admin - Metadata Schemas */}
        <Route
          path="/admin/metadata/schemas"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <SchemaListPage />
            </Suspense>
          }
        />
        <Route
          path="/admin/metadata/schemas/new"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <SchemaBuilderPage />
            </Suspense>
          }
        />
        <Route
          path="/admin/metadata/schemas/:id"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <SchemaBuilderPage />
            </Suspense>
          }
        />

        {/* Admin - System Configuration (incl. EPC prefix filters) */}
        <Route
          path="/admin/system-config"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <SystemConfigPage />
            </Suspense>
          }
        />

        {/* Admin - Device Management (handheld provisioning + lifecycle) */}
        <Route
          path="/admin/devices"
          element={
            <PermissionGuard module="deviceManagement">
              <Suspense fallback={<LoadingSpinner />}>
                <DevicesPage />
              </Suspense>
            </PermissionGuard>
          }
        />
        <Route
          path="/admin/devices/new"
          element={
            <PermissionGuard module="deviceManagement">
              <Suspense fallback={<LoadingSpinner />}>
                <DeviceFormPage />
              </Suspense>
            </PermissionGuard>
          }
        />
        <Route
          path="/admin/devices/:id"
          element={
            <PermissionGuard module="deviceManagement">
              <Suspense fallback={<LoadingSpinner />}>
                <DeviceDetailPage />
              </Suspense>
            </PermissionGuard>
          }
        />
        <Route
          path="/admin/devices/:id/edit"
          element={
            <PermissionGuard module="deviceManagement">
              <Suspense fallback={<LoadingSpinner />}>
                <DeviceFormPage />
              </Suspense>
            </PermissionGuard>
          }
        />

        {/* Admin - Dynamic Workflows (Phase 2) */}
        <Route
          path="/admin/workflows"
          element={
            <FeatureRoute feature="workflows">
              <Suspense fallback={<LoadingSpinner />}>
                <WorkflowsListPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/admin/workflows/import"
          element={
            <FeatureRoute feature="workflows">
              <Suspense fallback={<LoadingSpinner />}>
                <WorkflowImportPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/admin/workflows/bindings/new"
          element={
            <FeatureRoute feature="workflows">
              <Suspense fallback={<LoadingSpinner />}>
                <WorkflowBindingsPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/admin/workflows/:id"
          element={
            <FeatureRoute feature="workflows">
              <Suspense fallback={<LoadingSpinner />}>
                <WorkflowDetailPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/admin/workflows/designer"
          element={
            <FeatureRoute feature="workflows">
              <Suspense fallback={<LoadingSpinner />}>
                <WorkflowDesignerPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        {/* Phase 8 #13 — bulk-migrate in-flight instances from a prior
            definition version onto :id. The page exposes a state-mapping
            editor + dry-run preview before any writes hit the database. */}
        <Route
          path="/admin/workflows/:id/migrate"
          element={
            <FeatureRoute feature="workflows">
              <Suspense fallback={<LoadingSpinner />}>
                <WorkflowMigratePage />
              </Suspense>
            </FeatureRoute>
          }
        />

        {/* Shipments (Phase 2.5) — operator queues + lifecycle actions. The
            list has its own `shipments` flag (off on TOCA); the detail page
            needs only `workflows`, because dynamic modules and the asset trace
            link straight to it. */}
        <Route
          path="/shipments"
          element={
            <FeatureRoute feature={['workflows', 'shipments']}>
              <Suspense fallback={<LoadingSpinner />}>
                <ShipmentsPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/shipments/:id"
          element={
            <FeatureRoute feature="workflows">
              <Suspense fallback={<LoadingSpinner />}>
                <ShipmentDetailPage />
              </Suspense>
            </FeatureRoute>
          }
        />

        {/* A2 — WorkflowOrder (RFP/PO) pages: list with computed progress
            bars + detail with batch/backorder lineage. The list and the ERP
            admin follow the `orders` flag (off on TOCA); the detail page, which
            the client hub links to, needs only `workflows`. */}
        <Route
          path="/orders"
          element={
            <FeatureRoute feature={['workflows', 'orders']}>
              <Suspense fallback={<LoadingSpinner />}>
                <OrdersPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        {/* B-PULL — ERP pull connector admin. Registered before /orders/:id
            so the literal /orders/erp segment doesn't get captured as an
            order id. */}
        <Route
          path="/orders/erp"
          element={
            <FeatureRoute feature={['workflows', 'orders']}>
              <Suspense fallback={<LoadingSpinner />}>
                <ErpConnectionsPage />
              </Suspense>
            </FeatureRoute>
          }
        />
        <Route
          path="/orders/:id"
          element={
            <FeatureRoute feature="workflows">
              <Suspense fallback={<LoadingSpinner />}>
                <OrderDetailPage />
              </Suspense>
            </FeatureRoute>
          }
        />

        {/* Phase 11 — catch-all for tenant-authored modules. The page
            fetches the module config by :code and routes to one of the
            four built-in templates (Phase 12) by moduleType. Permission
            gating is enforced server-side: the GET /modules endpoint
            already strips modules the user can't view, so any code that
            lands here is one the user is allowed to see. */}
        <Route
          path="/dynamic/:code"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <DynamicModulePage />
            </Suspense>
          }
        />

        {/* Catalogs */}
        <Route
          path="/catalogs"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <CatalogHubPage />
            </Suspense>
          }
        />
        <Route path="/catalogs/users" element={<PermissionGuard module="users"><UsersPage /></PermissionGuard>} />
        <Route path="/catalogs/locations" element={<PermissionGuard module="locations"><LocationsPage /></PermissionGuard>} />
        <Route
          path="/catalogs/roles"
          element={
            <PermissionGuard module="roles">
              <Suspense fallback={<LoadingSpinner />}>
                <RolesPage />
              </Suspense>
            </PermissionGuard>
          }
        />
        <Route
          path="/catalogs/clients"
          element={
            <FeatureRoute feature="clients">
              <Suspense fallback={<LoadingSpinner />}>
                <ClientsPage />
              </Suspense>
            </FeatureRoute>
          }
        />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default App;
